import { mobileApiClient } from '@/utils/api'
import { getApiBaseUrl } from '@/config/api'
const apiBaseUrl = getApiBaseUrl()

const state = {
  documents: [],
  loading: false,
  error: null
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_DOCUMENTS(state, documents) {
    state.documents = documents
  },
  ADD_DOCUMENT(state, document) {
    state.documents.push(document)
  },
  REMOVE_DOCUMENT(state, documentId) {
    state.documents = state.documents.filter(doc => doc.id !== documentId)
  }
}

const actions = {
  // Upload profile picture
  async uploadProfilePicture({ commit }, formData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.post('/Documents/UploadDocumentFile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      return response
    } catch (error) {
      console.error('Profile picture upload error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to upload profile picture'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Upload document
  async uploadDocument({ commit }, formData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.post('/Documents/UploadDocumentFile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      if (!response.didError) {
        commit('ADD_DOCUMENT', response.model)
      }
      
      return response
    } catch (error) {
      console.error('Document upload error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to upload document'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get documents by employee
  async getDocumentsByEmployee({ commit }, employeeId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.get(`/Documents/ListproofdocumentbyEmployeeIDAsynch/${employeeId}`)
      
      if (!response.didError) {
        commit('SET_DOCUMENTS', response.model || [])
      }
      
      return response
    } catch (error) {
      console.error('Get documents error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to load documents'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Get documents by employee and reference (for leave requests)
  async getDocumentsByEmployeeAndReference({ commit }, { employeeId, referenceNo, proofTypeId = null }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      // Build the API endpoint
      let endpoint = `/Documents/getproofdocumentbyEmployeeIDandReferenceNoandTypeAsync/${employeeId}/${referenceNo}`
      if (proofTypeId) {
        endpoint += `/${proofTypeId}`
      }
      
      console.log('Calling documents API:', endpoint)
      
      const response = await mobileApiClient.get(endpoint)
      
      console.log('Documents API response:', response)
      
      if (!response.didError) {
        commit('SET_DOCUMENTS', response.model || [])
        return {
          didError: false,
          Model: response.model || []
        }
      } else {
        return {
          didError: true,
          errorMessage: response.errorMessage || 'Failed to load documents'
        }
      }
    } catch (error) {
      console.error('Get documents by employee and reference error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to load documents'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Delete document
  async deleteDocument({ commit }, documentId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.delete(`/Documents/delete_document/${documentId}`)
      
      if (!response.didError) {
        commit('REMOVE_DOCUMENT', documentId)
      }
      
      return response
    } catch (error) {
      console.error('Delete document error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to delete document'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Download document
  async downloadDocument({ commit }, documentId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentId}`, {
        responseType: 'blob'
      })
      
      return response
    } catch (error) {
      console.error('Download document error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to download document'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Download profile photo
  async downloadProfilePhoto({ commit }, documentId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const response = await mobileApiClient.get(`/Documents/DownloadProfilePhoto/${documentId}`, {
        responseType: 'blob'
      })
      
      return response
    } catch (error) {
      console.error('Download profile photo error:', error)
      const errorMessage = error.response?.data?.errorMessage || 'Failed to download profile photo'
      commit('SET_ERROR', errorMessage)
      return {
        didError: true,
        errorMessage: errorMessage
      }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Open document (supports both local and Firebase storage)
  async openDocument({ commit }, document) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      if (document.storagetype === 'firebase' && document.fileUrl) {
        return document.fileUrl;
      } else {
        // Fix: avoid double /api/api in the URL
        let base = apiBaseUrl.replace(/\/+$/, '') // remove trailing slash
        let path = ''
        if (/\/api$/i.test(base)) {
          path = `/Documents/DownloadDocumentFile/${document.docid}`
        } else {
          path = `/api/Documents/DownloadDocumentFile/${document.docid}`
        }
        return `${base}${path}`
      }
    } catch (error) {
      console.error('Error in openDocument action:', error);
      commit('SET_ERROR', error.message || 'Failed to open document');
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  }
}

// Helper function to get MIME type from file extension
function getMimeType(extension) {
  const mimeTypes = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.txt': 'text/plain',
    '.wav': 'audio/wav',
    '.mp3': 'audio/mpeg',
    '.mp4': 'video/mp4',
    '.avi': 'video/x-msvideo'
  }
  return mimeTypes[extension?.toLowerCase()] || 'application/octet-stream'
}

const getters = {
  documents: state => state.documents,
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 